// ---------------------------------------------------------------------------
// The sourcing search a wizard state stands for (R17a)
//
// One builder for every Apollo search the campaign wizard's sourcing makes:
// the lead step's preview (entering the step, Refresh, "Re-interpret ICP"),
// "Load 25 more", the CSV export, the sequence step's sample lead — and, on
// the server, preparing the campaign and topping it up
// (entrypoints/sdr/src/server/helpers/apollo-sourcing.ts). Each built its own
// before, and they drifted: only the launch sent the uploaded company list,
// so the preview showed leads from any company while the campaign was sourced
// inside the list; only the preview sent the revenue in whole dollars.
//
// It reads the state as the lead step persists it on the draft
// (`apolloSearchInput`): the form's fields, `autoFilled` (what the last ICP
// interpretation wrote into them) and `removedApolloPersonIds`. A value still
// exactly what the interpretation wrote is left out: the model reads it from
// the ICP again, and sending it as well would keep an old interpretation's
// values in every later search. Read defensively, as the draft is the
// browser's JSON.
//
// Pure: no Lit, no Adornis, no decorators, so the server can import it.
// ---------------------------------------------------------------------------

import { type ApolloLeadSearchInput, revenueUsd } from './apollo-api.js';
import { readRemovedApolloPersonIds, unionExcludeIds } from './sourcing-curation.js';

/** Every filter field of a sourcing search, in the order the wire carries them. */
export const SOURCING_FILTER_FIELDS = [
  'icpDescription',
  'companyLocation',
  'companyIndustry',
  'industryKeywords',
  'employeeCount',
  'revenueMin',
  'revenueMax',
  'jobTitle',
  'department',
  'personLocation',
  'organizationNames',
] as const;

/** The fields every search sends, empty or not; a revenue that is not set is left out. */
export const ALWAYS_SENT_FILTER_FIELDS = SOURCING_FILTER_FIELDS.filter(
  (f): f is Exclude<(typeof SOURCING_FILTER_FIELDS)[number], 'revenueMin' | 'revenueMax'> =>
    f !== 'revenueMin' && f !== 'revenueMax',
);

/**
 * The fields an ICP interpretation fills in. The ICP itself and the company
 * list are the operator's and never count as interpreted.
 */
export const AUTO_FILLED_FIELDS = [
  'companyLocation',
  'personLocation',
  'companyIndustry',
  'industryKeywords',
  'department',
  'employeeCount',
  'jobTitle',
  'revenueMin',
  'revenueMax',
] as const;

export interface SourcingFilters {
  icpDescription: string;
  companyLocation: string[];
  companyIndustry: string[];
  industryKeywords: string;
  employeeCount: string[];
  /** Whole US dollars; left out when the form has none. */
  revenueMin?: number;
  revenueMax?: number;
  jobTitle: string;
  department: string[];
  personLocation: string[];
  /** Companies to source inside (the file that narrows the search); empty means any company. */
  organizationNames: string[];
}

type Field = (typeof SOURCING_FILTER_FIELDS)[number];
const LIST_FIELDS: ReadonlySet<Field> = new Set([
  'companyLocation',
  'companyIndustry',
  'employeeCount',
  'department',
  'personLocation',
  'organizationNames',
]);
const REVENUE_FIELDS: ReadonlySet<Field> = new Set(['revenueMin', 'revenueMax']);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

/** A list of text; a list saved as one text (old drafts) is a list of it. */
const list = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string');
  if (typeof value === 'string' && value.trim()) return [value.trim()];
  return [];
};

/** Millions as the form holds them, or null. */
const millions = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** One field as the form holds it, so a draft value and the interpretation's snapshot compare alike. */
const read = (field: Field, value: unknown): string | string[] | number | null =>
  LIST_FIELDS.has(field) ? list(value) : REVENUE_FIELDS.has(field) ? millions(value) : text(value);

/** Whether `field` still holds exactly what the interpretation wrote into it. */
function stillInterpreted(field: Field, draft: Record<string, unknown>, autoFilled: Record<string, unknown>): boolean {
  if (!(AUTO_FILLED_FIELDS as readonly string[]).includes(field) || autoFilled[field] === undefined) return false;
  return JSON.stringify(read(field, draft[field])) === JSON.stringify(read(field, autoFilled[field]));
}

/** The search filters a wizard state stands for. Never throws; an empty state is a search with every field empty. */
export function sourcingFilters(draft: unknown): SourcingFilters {
  const state = isRecord(draft) ? draft : {};
  const autoFilled = isRecord(state.autoFilled) ? state.autoFilled : {};
  const value = (field: Field) => (stillInterpreted(field, state, autoFilled) ? undefined : state[field]);
  const filters: SourcingFilters = {
    icpDescription: text(state.icpDescription),
    companyLocation: list(value('companyLocation')),
    companyIndustry: list(value('companyIndustry')),
    industryKeywords: text(value('industryKeywords')),
    employeeCount: list(value('employeeCount')),
    jobTitle: text(value('jobTitle')),
    department: list(value('department')),
    personLocation: list(value('personLocation')),
    organizationNames: list(state.organizationNames),
  };
  const revenueMin = revenueUsd(millions(value('revenueMin')));
  const revenueMax = revenueUsd(millions(value('revenueMax')));
  if (revenueMin !== undefined) filters.revenueMin = revenueMin;
  if (revenueMax !== undefined) filters.revenueMax = revenueMax;
  return filters;
}

export interface SourcingSearchOptions {
  page: number;
  perPage: number;
  /** People never to return besides the removed ones the state holds, e.g. the project's earlier leads. */
  excludeApolloPersonIds?: Iterable<string>;
  /** Ask the model for a fresh interpretation ("Re-interpret ICP"). */
  forceRemap?: boolean;
}

/**
 * The search the browser sends for a wizard state: its filters, a page, and
 * the people never to return — the ones the operator removed from the
 * preview (as the launch leaves them out) and those given.
 */
export function sourcingSearch(draft: unknown, opts: SourcingSearchOptions): ApolloLeadSearchInput {
  const search: ApolloLeadSearchInput = { ...sourcingFilters(draft), page: opts.page, perPage: opts.perPage };
  if (opts.forceRemap) search.forceRemap = true;
  const excluded = unionExcludeIds(opts.excludeApolloPersonIds ?? [], readRemovedApolloPersonIds(draft));
  if (excluded.length > 0) search.excludeApolloPersonIds = excluded;
  return search;
}
